// packages/ui/src/focus.ts
import { topDialogContainer } from './dialogStack';

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// Matching the selector is not enough: a control that is hidden (display none, a
// responsive `sm:hidden`), inside a disabled fieldset or inert cannot take focus, and
// as the first or last item it would let Tab walk out of the dialog.
function canTakeFocus(el: HTMLElement): boolean {
  if (el.closest('[hidden], [inert], fieldset[disabled]')) return false;
  // Not in every environment (jsdom has none); without it, assume visible.
  return typeof el.checkVisibility !== 'function' || el.checkVisibility();
}

export function focusableIn(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(canTakeFocus);
}

/** Move focus into a freshly opened dialog unless something inside (an autoFocus) already took it. */
export function focusInitial(dialog: HTMLElement, body?: HTMLElement | null): void {
  if (dialog.contains(document.activeElement)) return;
  (focusableIn(body ?? null)[0] ?? focusableIn(dialog)[0] ?? dialog).focus();
}

/**
 * Tab / Shift+Tab wrap inside `dialog`. Focusables are queried at keypress time, never cached, so
 * content that arrives later (a lazily loaded editor) is covered.
 */
export function trapTab(e: KeyboardEvent, dialog: HTMLElement): void {
  if (e.key !== 'Tab') return;
  const items = focusableIn(dialog);
  if (items.length === 0) {
    e.preventDefault();
    dialog.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  // Focus outside, or on the shell after a step swap removed the focused control: pull it back in.
  if (!active || !dialog.contains(active) || active === dialog) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  } else if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

/**
 * Give focus back to what had it before the dialog opened. If that element is gone, land in the
 * layer still open beneath (its first control), else let focus rest on the page.
 */
export function restoreFocus(opener: HTMLElement | null): void {
  if (opener?.isConnected) {
    opener.focus();
    return;
  }
  const beneath = topDialogContainer();
  if (beneath) (focusableIn(beneath)[0] ?? beneath).focus();
}

/** The element to restore to: whatever has focus now, if it is a real element. */
export function currentOpener(): HTMLElement | null {
  return typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
    && document.activeElement !== document.body
    ? document.activeElement
    : null;
}
